import React, { useState, useEffect, useRef } from "react";
import {
    Button,
    Grid,
    List,
    ListItem,
    Box,
    ListItemButton,
    Typography,
    Divider,
    alpha,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomModal from "../../CustomModal";
import FilterListIcon from "@mui/icons-material/FilterList";
import ChildFilterWrapper from "./ChildFilterWrapper";
import * as dayjs from "dayjs";

const ChildFiltersModal = ({ selectedFilter, filters, selectedColumn, selectedFilterValue, setFilters, open, close, loading, setLoading, endingLabel, metadata, currentParentFilter }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [gridSelection, setGridSelection] = useState({});
    const [selectionModel, setSelectionModel] = useState([]);
    const [dateError, setDateError] = useState(false);
    const [activeSection, setActiveSection] = useState(0);
    const [existingSubFilters, setExistingSubFilters] = useState({});
    const childRefs = useRef([]);
    const contentRef = useRef(null);

    const theme = useTheme();

    // reset state upon modal open or when currentParentFilter changes
    useEffect(() => {
        if (open && currentParentFilter) {
            // Load existing selections for this specific parent filter
            const existingSelections = {};
            const existingSelectionModel = {};
            const existingFilters = {};

            if (currentParentFilter.rows && Array.isArray(currentParentFilter.rows)) {
                // Try multiple matching strategies
                let matchingRow = null;

                // Match by ID
                if (selectedFilterValue?.id) {
                    matchingRow = currentParentFilter.rows.find(row => row.id === selectedFilterValue.id);
                }

                // Match by value to handle wildcards
                if (!matchingRow) {
                    const valueToMatch = selectedFilterValue?.value || selectedFilterValue;
                    matchingRow = currentParentFilter.rows.find(row => {
                        const rowValue = row.value || row[currentParentFilter.key] ||
                            (row.field ? row[row.field.split('.').pop()] : null);
                        return rowValue === valueToMatch ||
                            (valueToMatch === "*" && row.operator === "contains" && currentParentFilter.rows.length === 1);
                    });
                }

                // Match by display value
                if (!matchingRow && selectedFilterValue?.displayValue) {
                    matchingRow = currentParentFilter.rows.find(row => {
                        return row.displayValue === selectedFilterValue.displayValue;
                    });
                }

                if (matchingRow && matchingRow.rows && Array.isArray(matchingRow.rows)) {

                    matchingRow.rows.forEach((childFilterGroup) => {
                        Object.entries(childFilterGroup).forEach(([key, values]) => {
                            if (Array.isArray(values)) {
                                existingSelections[key] = values;
                                existingFilters[key] = values; // Store for display
                                existingSelectionModel[key] = values.map(v => v.id || `${key}_${v.value}`);
                            }
                        });
                    });
                }
            }

            setGridSelection(existingSelections);
            setSelectionModel(existingSelectionModel);
            setExistingSubFilters(existingFilters);
        } else if (!open) {
            // Clear state when modal closes
            setGridSelection({});
            setSelectionModel({});
            setSelectedRows([]);
            setActiveSection(0);
            setExistingSubFilters({});
        }
    }, [open, currentParentFilter, selectedFilterValue]);

    // Intersection Observer for scroll highlighting
    useEffect(() => {
        if (!open || !selectedFilter?.childFilters?.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = childRefs.current.findIndex(ref => ref === entry.target);
                        if (index !== -1) {
                            setActiveSection(index);
                        }
                    }
                });
            },
            {
                root: contentRef.current,
                rootMargin: '-50% 0px -50% 0px',
                threshold: 0
            }
        );

        childRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            childRefs.current.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, [open, selectedFilter?.childFilters]);

    const selectionChangedHandler = (key, values) => {
        setGridSelection((prevState) => {
            if (values && Object.keys(values)?.length > 0) {
                const updatedSelection = {
                    ...prevState,
                    ...values,
                };
                return updatedSelection;
            }
            const newSelection = { ...prevState };
            delete newSelection[key];
            return newSelection;
        });
    };

    const handleSaveFilter = () => {
        const filtersCopy = structuredClone(filters);
        const foundFilter = filtersCopy.find(
            (f) => f.id === currentParentFilter.id
        );

        if (foundFilter) {
            const selectionsArr = [];

            if (Object.keys(gridSelection).length > 0) {
                for (const elm in gridSelection) {
                    selectionsArr.push({ [elm]: gridSelection[elm] });
                }
            }

            // Find the specific row that matches the selected filter value
            foundFilter.rows = foundFilter.rows.map((row) => {
                const rowKey = foundFilter.key;
                const rowValue = row[rowKey] || row.value;

                // Check if this is the row we're adding child filters to
                if (rowValue === selectedFilterValue ||
                    (selectedFilterValue === "*" && row.operator === "contains" && foundFilter.rows.length === 1)) {
                    return {
                        ...row,
                        rows: selectionsArr
                    };
                }
                return row;
            });

            setFilters(filtersCopy);
        }

        setGridSelection({});
        setSelectionModel({});
        setExistingSubFilters({});
        close();
    };

    const validateDate = (value) => {
        if (Array.isArray(value)) {
            // Check if both dates are present and valid
            const isValidRange = value[0] && value[1] && value[0].isValid() && value[1].isValid();
            setDateError(!isValidRange);
            return isValidRange;
        } else {
            const isValid = value && dayjs(value).isValid();
            setDateError(!isValid);
            return isValid;
        }
    };

    const handleClose = () => {
        setGridSelection({});
        setSelectionModel({});
        setExistingSubFilters({});
        close();
    };

    const scrollToComponent = (index) => {
        setActiveSection(index);
        childRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };


    // Create actions for CustomModal
    const modalActions = (
        <>
            <Button
                variant="outlined"
                sx={{
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        backgroundColor: theme.palette.action.hover,
                    },
                }}
                onClick={() => handleClose()}
            >
                Close
            </Button>
            <Button
                color="primary"
                variant="contained"
                sx={{ margin: "5px" }}
                disabled={Object.keys(gridSelection).length === 0 && Object.keys(selectionModel).length === 0}
                onClick={() => handleSaveFilter()}
            >
                Save Changes
            </Button>
        </>
    );

    return (
        <CustomModal
            open={open}
            onClose={() => close()}
            title="Additional Filters"
            subtitle={`Configure sub-filters for ${selectedFilterValue === "*" ? "All " + currentParentFilter?.category : selectedFilterValue}`}
            icon={<FilterListIcon sx={{ color: 'white', fontSize: 20 }} />}
            maxWidth="xl"
            contentPadding={false}
            actions={modalActions}
        >
            <Box sx={{
                display: "flex",
                minHeight: "600px",
                height: "600px",
                backgroundColor: theme.palette.surface.page
            }}>
                <Box sx={{
                    flexGrow: 0,
                    flexShrink: 0,
                    width: '220px',
                    position: 'sticky',
                    top: '0px',
                    height: "600px",
                    overflowY: 'auto',
                    backgroundColor: theme.palette.surface.subtle,
                    borderRight: `1px solid ${theme.palette.divider}`,
                    padding: '20px 16px',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.surface.borderStrong,
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: theme.palette.grey[400],
                        },
                    },
                }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                fontSize: '1rem',
                                mb: 1
                            }}
                        >
                            Sub Filters
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.875rem',
                                lineHeight: 1.4
                            }}
                        >
                            Configure additional criteria for your filter
                        </Typography>
                    </Box>
                    <List component="nav" sx={{ padding: 0 }}>
                        {selectedFilter?.childFilters?.map((filter, index) => (
                            <ListItemButton
                                selected={activeSection === index}
                                onClick={() => scrollToComponent(index)}
                                key={index}
                                sx={{
                                    borderRadius: '8px',
                                    mb: 1,
                                    padding: '12px 16px',
                                    transition: 'all 0.2s ease-in-out',
                                    position: 'relative',
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '3px',
                                        height: '20px',
                                        backgroundColor: 'transparent',
                                        borderRadius: '0 2px 2px 0',
                                        transition: 'background-color 0.2s ease-in-out',
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                        '&:before': {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        }
                                    },
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.action.hover, 0.04),
                                        transform: 'translateX(2px)',
                                    },
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    width: '100%'
                                }}>
                                    <Box sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        backgroundColor: activeSection === index
                                            ? '#04aeea'
                                            : theme.palette.grey[300],
                                        transition: 'background-color 0.2s ease-in-out',
                                        flexShrink: 0
                                    }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'inherit',
                                            fontSize: '0.875rem',
                                            fontWeight: activeSection === index ? 500 : 400,
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {filter.friendlyName}
                                    </Typography>
                                </Box>
                            </ListItemButton>
                        ))}
                    </List>
                </Box>
                <Box sx={{
                    flexGrow: 1,
                    height: "600px",
                    overflowY: 'auto',
                    padding: '24px',
                    backgroundColor: 'transparent'
                }}>
                    {selectedFilter?.childFilters?.map((filter, index) => {
                        return (
                            <Box
                                key={index.toString()}
                                ref={(el) => (childRefs.current[index] = el)}
                                sx={{
                                    mb: index < selectedFilter.childFilters.length - 1 ? 3 : 0,
                                    scrollMarginTop: '24px'
                                }}
                            >
                                <ChildFilterWrapper
                                    filter={filter}
                                    parentValue={selectedFilterValue}
                                    setLoading={setLoading}
                                    loading={loading}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                    modalOpen={open}
                                    gridSelection={gridSelection}
                                    setGridSelection={setGridSelection}
                                    selectionChangedHandler={selectionChangedHandler}
                                    selectedColumn={selectedColumn}
                                    selectionModel={selectionModel}
                                    setSelectionModel={setSelectionModel}
                                    setFilters={setFilters}
                                    endingLabel={endingLabel}
                                    metadata={metadata}
                                    currentParentFilter={currentParentFilter}
                                    isChildFilterNumeric={selectedFilter}
                                    validateDate={validateDate}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </CustomModal>
    );
}

export default ChildFiltersModal;